
class HdtFilterDropdown extends HTMLElement {
  static MOBILE_MQ = '(max-width: 767px)';
  static PRICE_FILTER_KEYS = ['filter.v.price.gte', 'filter.v.price.lte'];

  static isMobileViewport() {
    return window.matchMedia(HdtFilterDropdown.MOBILE_MQ).matches;
  }

  static updateCheckboxVisual(input) {
    const item = input?.closest('.hdt-filter-group__list-item');
    if (!item) return;
    item.classList.toggle('is-checked', input.checked);
  }

  static syncAllCheckboxVisuals(root) {
    root?.querySelectorAll('.hdt-filter-group__list-item input[type="checkbox"]').forEach((input) => {
      HdtFilterDropdown.updateCheckboxVisual(input);
    });
  }

  makeDrawerCheckboxIdsUnique() {
    this.querySelectorAll('.hdt-filter-group__list-item input[type="checkbox"]').forEach((input) => {
      const label = input.nextElementSibling;
      if (!input.id || input.dataset.dthDrawerId) return;

      input.dataset.dthDrawerId = '1';
      input.dataset.dthOrigId = input.id;
      input.id = `dth-drawer-${input.id}`;

      if (label?.htmlFor) {
        label.dataset.dthOrigFor = label.htmlFor;
        label.htmlFor = input.id;
      }
    });
  }

  restoreDrawerCheckboxIds() {
    this.querySelectorAll('input[data-dth-drawer-id]').forEach((input) => {
      if (input.dataset.dthOrigId) {
        input.id = input.dataset.dthOrigId;
      }
      delete input.dataset.dthDrawerId;
      delete input.dataset.dthOrigId;

      const label = input.nextElementSibling;
      if (label?.dataset.dthOrigFor) {
        label.htmlFor = label.dataset.dthOrigFor;
        delete label.dataset.dthOrigFor;
      }
    });
  }

  setSidebarFacetInert(inert) {
    const sidebarForm = document.querySelector('hdt-facet-filters-form form#hdt-facet-filters-form-sidebar');
    const sidebarFacet = sidebarForm?.closest('hdt-facet-filters-form');
    if (!sidebarFacet) return;
    if (inert) {
      sidebarFacet.setAttribute('inert', '');
    } else {
      sidebarFacet.removeAttribute('inert');
    }
  }

  bindMobileCheckboxVisuals() {
    const form = this.getFacetFormEl()?.querySelector('form');
    if (!form) return;

    this._onMobileFormChangeCapture = (event) => {
      const input = event.target;
      if (!input.matches('input[type="checkbox"]') || !this.contains(input)) return;

      event.stopImmediatePropagation();
      HdtFilterDropdown.updateCheckboxVisual(input);
      this._mobileFormDirty = true;
    };

    this._onMobileItemClick = (event) => {
      const item = event.target.closest('.hdt-filter-group__list-item');
      if (!item || !this.contains(item)) return;

      const input = item.querySelector('input[type="checkbox"]');
      if (!input) return;

      queueMicrotask(() => {
        HdtFilterDropdown.updateCheckboxVisual(input);
        this._mobileFormDirty = true;
      });
    };

    form.addEventListener('change', this._onMobileFormChangeCapture, true);
    this.addEventListener('click', this._onMobileItemClick);
  }

  unbindMobileCheckboxVisuals() {
    const form = this.getFacetFormEl()?.querySelector('form');
    if (form && this._onMobileFormChangeCapture) {
      form.removeEventListener('change', this._onMobileFormChangeCapture, true);
    }
    if (this._onMobileItemClick) {
      this.removeEventListener('click', this._onMobileItemClick);
    }
    this._onMobileFormChangeCapture = null;
    this._onMobileItemClick = null;
  }

  static buildFacetUrl(facetFormEl) {
    const form = facetFormEl?.querySelector('form');
    if (!form) return null;

    const url = new URL(form.action);

    for (const [key, value] of new FormData(form)) {
      url.searchParams.append(key, value);
    }

    url.searchParams.delete('page');

    HdtFilterDropdown.PRICE_FILTER_KEYS.forEach((key) => {
      if (url.searchParams.get(key) === '') {
        url.searchParams.delete(key);
      }
    });

    url.searchParams.set('section_id', facetFormEl.getAttribute('section-id'));
    return url;
  }

  static installFacetRenderPatch() {
    if (HdtFilterDropdown._facetPatchInstalled) return;
    HdtFilterDropdown._facetPatchInstalled = true;

    document.addEventListener(
      'facet:render',
      () => {
        const filterEl = document.getElementById('hdt-filter_form');
        if (!filterEl || filterEl.tagName !== 'HDT-FILTER-DROPDOWN') return;

        const parent = filterEl.parentElement;
        if (!parent || typeof parent.close === 'function') return;

        parent.close = () => {
          if (typeof filterEl.close === 'function') filterEl.close();
        };
      },
      true
    );
  }

  connectedCallback() {
    this.panel = this.querySelector('.hdt-filter-dropdown__panel');
    this.backdrop = this.querySelector('.hdt-filter-dropdown__backdrop');
    this.closeBtn = this.querySelector('.hdt-filter-dropdown__close');
    this.mq = window.matchMedia(HdtFilterDropdown.MOBILE_MQ);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onMqChange = this.onMqChange.bind(this);
    this._mobileFormDirty = false;

    this.closeBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.close();
    });

    this.backdrop?.addEventListener('click', () => this.close());

    this.mq.addEventListener('change', this.onMqChange);
    HdtFilterDropdown.ensureFacetHooks();
    this.syncTriggerExpanded();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    this.mq.removeEventListener('change', this.onMqChange);
    this.clearOutsideClickListener();
    this.unbindMobileCheckboxVisuals();
    this.removeOrphanAnchor();
    this.restoreMobileFacetForm();
  }

  getFacetFormEl() {
    return this.querySelector('hdt-facet-filters-form');
  }

  setMobileFacetDeferred(shouldDefer) {
    const facetFormEl = this.getFacetFormEl();
    if (!facetFormEl) return;

    if (shouldDefer) {
      if (facetFormEl._dthHadRenderOnChange == null) {
        facetFormEl._dthHadRenderOnChange = facetFormEl.hasAttribute('render-on-change');
      }
      facetFormEl.removeAttribute('render-on-change');
      return;
    }

    this.restoreMobileFacetForm();
  }

  restoreMobileFacetForm() {
    const facetFormEl = this.getFacetFormEl();
    if (!facetFormEl || facetFormEl._dthHadRenderOnChange == null) return;

    if (facetFormEl._dthHadRenderOnChange) {
      facetFormEl.setAttribute('render-on-change', '');
    } else {
      facetFormEl.removeAttribute('render-on-change');
    }

    facetFormEl._dthHadRenderOnChange = null;
  }

  applyMobileFilters() {
    if (!this._mobileFormDirty) return;

    const facetFormEl = this.getFacetFormEl();
    const form = facetFormEl?.querySelector('form');
    const url = HdtFilterDropdown.buildFacetUrl(facetFormEl);

    if (!form || !url) return;

    this._mobileFormDirty = false;
    this._applyingMobileFilters = true;

    form.dispatchEvent(
      new CustomEvent('facet:render', {
        bubbles: true,
        detail: { url },
      })
    );
  }

  static ensureFacetHooks() {
    if (HdtFilterDropdown._facetHooksReady) return;
    HdtFilterDropdown._facetHooksReady = true;

    document.addEventListener('click', (event) => {
      const btn = event.target.closest('[aria-controls="hdt-filter_form"]');
      if (!btn || btn.closest('hdt-filter-dropdown')) return;

      const dropdown = HdtFilterDropdown.getInstance();
      if (!dropdown) return;

      event.preventDefault();
      event.stopPropagation();
      dropdown.toggle();
    });

    document.addEventListener('products:update', () => {
      HdtFilterDropdown.onProductsUpdate();

      if (!HdtFilterDropdown.isMobileViewport()) return;
      const dropdown =
        document.querySelector('hdt-filter-dropdown.is-open') || HdtFilterDropdown.getInstance();
      if (dropdown) {
        HdtFilterDropdown.syncAllCheckboxVisuals(dropdown);
      }
    });

    document.addEventListener('shopify:section:load', () => {
      HdtFilterDropdown.getInstance()?.syncTriggerExpanded();
    });
  }

  static getInstance() {
    return (
      document.querySelector('[main-content] hdt-filter-dropdown#hdt-filter_form') ||
      document.querySelector('hdt-filter-dropdown#hdt-filter_form')
    );
  }

  static onProductsUpdate() {
    HdtFilterDropdown.cleanupOrphans();

    const dropdown = HdtFilterDropdown.getInstance();
    if (!dropdown?.isOpen) return;

    if (dropdown._applyingMobileFilters) {
      dropdown._applyingMobileFilters = false;
      return;
    }

    if (HdtFilterDropdown.isMobileViewport()) return;

    requestAnimationFrame(() => dropdown.close());
  }

  static cleanupOrphans() {
    const main = document.querySelector('[main-content]');
    if (!main) return;

    document.querySelectorAll('hdt-filter-dropdown').forEach((el) => {
      if (main.contains(el)) return;
      if (el._movedToBody) return;
      el.removeOrphanAnchor();
      el.remove();
    });
  }

  get isMobile() {
    return this.mq.matches;
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  syncTriggerExpanded() {
    const expanded = this.isOpen ? 'true' : 'false';
    document.querySelectorAll('[aria-controls="hdt-filter_form"]').forEach((btn) => {
      if (btn.closest('hdt-filter-dropdown') === this) return;
      btn.setAttribute('aria-expanded', expanded);
    });
  }

  onMqChange() {
    if (this.isOpen && !this.isMobile) {
      this.unbindMobileCheckboxVisuals();
      this.restorePlacement();
      this.unlockScroll();
      this.setMobileFacetDeferred(false);
    } else if (this.isOpen && this.isMobile) {
      this.moveToBody();
      this.lockScroll();
      this.setMobileFacetDeferred(true);
    }
  }

  moveToBody() {
    if (!this.isMobile || this._movedToBody) return;
    this._anchor = document.createComment('hdt-filter-dropdown-anchor');
    this.parentNode?.insertBefore(this._anchor, this);
    document.body.appendChild(this);
    this._movedToBody = true;
  }

  restorePlacement() {
    if (!this._movedToBody || !this._anchor?.parentNode) return;
    this._anchor.parentNode.insertBefore(this, this._anchor);
    this.removeOrphanAnchor();
  }

  removeOrphanAnchor() {
    this._anchor?.remove();
    this._anchor = null;
    this._movedToBody = false;
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.classList.add('is-open');
    this.setAttribute('open', '');
    this.removeAttribute('aria-hidden');
    this.syncTriggerExpanded();

    if (this.isMobile) {
      this._mobileFormDirty = false;
      this.setMobileFacetDeferred(true);
      this.moveToBody();
      this.makeDrawerCheckboxIdsUnique();
      this.setSidebarFacetInert(true);
      this.lockScroll();
      this.bindMobileCheckboxVisuals();
      requestAnimationFrame(() => HdtFilterDropdown.syncAllCheckboxVisuals(this));
    }

    this.scheduleOutsideClickListener();
    document.addEventListener('keydown', this.onKeydown);
  }

  close() {
    const wasMobileOpen = this.isMobile && this.isOpen;

    if (wasMobileOpen) {
      this.unbindMobileCheckboxVisuals();
      this.applyMobileFilters();
      this.setMobileFacetDeferred(false);
      this.restoreDrawerCheckboxIds();
      this.setSidebarFacetInert(false);
    }

    this.classList.remove('is-open');
    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    this.syncTriggerExpanded();
    this.restorePlacement();
    this.unlockScroll();
    this.clearOutsideClickListener();
    document.removeEventListener('keydown', this.onKeydown);
  }

  scheduleOutsideClickListener() {
    this.clearOutsideClickListener();
    this._outsideClickFrame = requestAnimationFrame(() => {
      this._outsideClickFrame = null;
      document.addEventListener('click', this.onDocumentClick);
    });
  }

  clearOutsideClickListener() {
    if (this._outsideClickFrame) {
      cancelAnimationFrame(this._outsideClickFrame);
      this._outsideClickFrame = null;
    }
    document.removeEventListener('click', this.onDocumentClick);
  }

  lockScroll() {
    document.documentElement.classList.add('hdt-filter-drawer-open');
  }

  unlockScroll() {
    document.documentElement.classList.remove('hdt-filter-drawer-open');
  }

  onDocumentClick(event) {
    if (this.isMobile) return;
    if (this.contains(event.target)) return;
    if (
      [...document.querySelectorAll('[aria-controls="hdt-filter_form"]')].some((btn) =>
        btn.contains(event.target)
      )
    ) {
      return;
    }
    this.close();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
    }
  }
}

if (!customElements.get('hdt-filter-dropdown')) {
  customElements.define('hdt-filter-dropdown', HdtFilterDropdown);
}

HdtFilterDropdown.installFacetRenderPatch();
